/**
 * Configuration utility functions.
 */

import { DEFAULT_CHART_CONFIG } from './constants';
import { CHART_PRESETS, type ChartPreset } from './presets';
import type { ChartConfig, DeepPartial } from './types';

/**
 * Check if a value is a plain object (not array, null, etc.)
 */
const isPlainObject = (value: unknown): value is Record<string, unknown> => {
    return (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        Object.prototype.toString.call(value) === '[object Object]'
    );
};

/**
 * Deep merge two objects, with source values overriding target values.
 * Only merges plain objects, not arrays or other complex types.
 *
 * @param target - Base object to merge into
 * @param source - Object with override values
 * @returns Merged object
 */
export function deepMerge<T>(target: T, source: DeepPartial<T>): T {
    if (!isPlainObject(target) || !isPlainObject(source)) {
        return (source ?? target) as T;
    }

    const result = { ...target } as Record<string, unknown>;
    const sourceObj = source as Record<string, unknown>;
    const targetObj = target as Record<string, unknown>;

    for (const key of Object.keys(sourceObj)) {
        const sourceVal = sourceObj[key];
        const targetVal = targetObj[key];

        if (sourceVal === undefined) {
            continue;
        }

        if (isPlainObject(sourceVal) && isPlainObject(targetVal)) {
            result[key] = deepMerge(targetVal, sourceVal);
        } else {
            result[key] = sourceVal;
        }
    }

    return result as T;
}

/**
 * Create a complete ChartConfig by merging defaults with optional preset and overrides.
 *
 * @param preset - Optional preset name to apply
 * @param overrides - Optional manual overrides (applied after preset)
 * @returns Complete ChartConfig
 *
 * @example
 * // Default config
 * const config = createChartConfig();
 *
 * @example
 * // Snappy preset
 * const config = createChartConfig('snappy');
 *
 * @example
 * // Smooth preset with custom tension
 * const config = createChartConfig('smooth', {
 *     physics: { tension: 18 }
 * });
 */
export const createChartConfig = (
    preset?: ChartPreset,
    overrides?: DeepPartial<ChartConfig>,
): ChartConfig => {
    let config = { ...DEFAULT_CHART_CONFIG };

    // Apply preset if specified
    if (preset && CHART_PRESETS[preset]) {
        config = deepMerge(config, CHART_PRESETS[preset]);
    }

    // Apply manual overrides if specified
    if (overrides) {
        config = deepMerge(config, overrides);
    }

    return config;
};
